import { useState, useMemo, memo, useEffect, useCallback } from 'react'

import { Table } from './table'
import { ASC, DESC } from './constants'
import { getColumnActions, getFilteredData } from './utils'
import { Container, Wrapper, Controls, ActionButton } from './components'
import { Export } from './export'
import { Search } from './search'
import { Pagination } from './pagination'
import { Skeleton } from './skeleton'

const GridComponent = ({
  data,
  columns,
  options,
  actions,
  rowIdKey,
  onClickRow,
  onSelectRow,
  onClearSearch,
  onChangePageSize,
  defaultSortBy,
  isRowDisabled,
  NoSearchResultsComponent,
  defaultPage = 1,
  isPagerVisible = true,
  isRowSelectable = false,
  shouldClearSelected = false,
  searchType = 'fuzzy',
  defaultSortOrder = ASC,
  defaultPageSize = Pagination.lengths[0]
}) => {
  const [sortBy, setSortBy] = useState(defaultSortBy || columns[0].id)
  const [sortOrder, setSortOrder] = useState(defaultSortOrder)
  const [page, setPage] = useState(defaultPage)
  const [pageSize, setPageSize] = useState(defaultPageSize)
  const [filteredData, setFilteredData] = useState(data)
  const [query, setQuery] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedRows, setSelectedRows] = useState([])
  const { sorters, searchKeys } = useMemo(() => getColumnActions(columns), [columns])
  const headers = useMemo(
    () =>
      columns.filter(({ label }) => label.length).map(({ id, label }) => ({ id, label })),
    [columns]
  )

  //This was added to allow for the setting of the sort order/column outside of the component
  useEffect(() => {
    setSortBy(defaultSortBy)
    setSortOrder(defaultSortOrder)
  }, [defaultSortOrder, defaultSortBy])
  const sortedFilteredData = useMemo(() => {
    if (filteredData) {
      return filteredData.sort((rowA, rowB) => {
        const sorter = sorters[sortBy]

        if (!sorter) {
          return 0
        }

        return sorter(rowA, rowB, sortOrder)
      })
    }

    return []
  }, [filteredData, sorters, sortBy, sortOrder])
  const {
    csvExportName,
    isSearchQueryHighlighted,
    disableFilters,
    fullWidth = false
  } = options
  const offset = (page - 1) * pageSize
  const visibleRows = sortedFilteredData.slice(offset, offset + pageSize)
  const onSort = useCallback(
    id => {
      if (id !== sortBy) {
        setSortOrder(ASC)
        setSortBy(id)
      } else {
        setSortOrder(sortOrder === ASC ? DESC : ASC)
      }
    },
    [sortBy, sortOrder]
  )
  const renderTable = () => {
    if (visibleRows.length) {
      return (
        <Table
          data={visibleRows}
          columns={columns}
          query={searchTerm}
          rowIdKey={rowIdKey}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onClickRow={onClickRow}
          onSelectRow={onSelectRow}
          isRowDisabled={isRowDisabled}
          isSelectable={isRowSelectable}
          isSearchQueryHighlighted={isSearchQueryHighlighted}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          onSort={onSort}
        />
      )
    }

    if (query) {
      return <NoSearchResultsComponent searchTerm={searchTerm} />
    }

    return <Skeleton columns={headers.length} />
  }
  const onSearchClear = useCallback(() => {
    const prevSearchTerm = searchTerm

    setQuery('')
    setSearchTerm('')
    setFilteredData(data)
    onClearSearch(prevSearchTerm)
  }, [data, searchTerm, onClearSearch])
  const onSearchChange = useCallback(
    value => {
      setQuery(value)

      if (!value) {
        onSearchClear()
      }
    },
    [onSearchClear]
  )
  const onRangeClear = useCallback(() => {
    let filtered = data

    if (query) {
      filtered = getFilteredData({
        data,
        searchKeys,
        searchType,
        query: query.trim()
      })
    }

    setFilteredData(filtered)
  }, [data, query, searchKeys, searchType])
  const onSearchSubmit = useCallback(() => {
    const filtered = getFilteredData({
      data,
      searchKeys,
      searchType,
      query: query.trim()
    })

    setSearchTerm(query)
    setFilteredData(filtered)

    if (filtered.length) {
      setPage(1)
    }
  }, [data, query, searchKeys, searchType])
  const handleOnChangePageSize = useCallback(
    size => {
      setPageSize(size)
      setPage(1)

      onChangePageSize(size)
    },
    [onChangePageSize]
  )

  useEffect(() => {
    setFilteredData(data)
  }, [data])

  useEffect(() => {
    if (shouldClearSelected) {
      setSelectedRows([])
    }
  }, [shouldClearSelected])

  return (
    <Container>
      <Wrapper fullWidth={fullWidth}>
        {!disableFilters && (
          <Controls>
            <Search
              query={query}
              isDisabled={!data}
              onChange={onSearchChange}
              onClear={onSearchClear}
              onSubmit={onSearchSubmit}
              onRangeClear={onRangeClear}
            />
            <Export
              headers={headers}
              data={sortedFilteredData}
              filename={csvExportName}
            />
            {Array.isArray(actions) &&
              actions.map(action => (
                <ActionButton
                  isDisabled={!selectedRows.length}
                  key={action.label}
                  isTextAlignLeft
                  size="small"
                  onClick={() => action.onAct({ selectedRows })}
                  icon={action.icon}>
                  {action.label}
                </ActionButton>
              ))}
          </Controls>
        )}
        {renderTable()}
      </Wrapper>
      {isPagerVisible && visibleRows.length ? (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={filteredData.length}
          onChangePage={setPage}
          onChangePageSize={handleOnChangePageSize}
        />
      ) : null}
    </Container>
  )
}
const Grid = memo(GridComponent)

export { Grid, GridComponent }
